'use client';
import { useDebouncedCallback } from '@react-hookz/web';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import { MESSAGES_COUNT } from '~constants/dialogs';
import { routes } from '~constants/routes';
import { BotPaywallContextProvider } from '~context/botPaywall/provider';
import { useChatReviewContext } from '~context/chatReview';
import { MediaContentContextProvider } from '~context/media-content/provider';
import { MessageListHeader, MessageListV2 } from '~entities/messages';
import { CheckoutReason, FeedbackTrigger } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { NUMBER_OF_SENT_MESSAGES_FOR_REVIEW } from '~features/chatReview';
import { BotPaywallContextRecipient, useShowPaywall } from '~features/paywall';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useGetContacts } from '~hooks/useGetContacts';
import { useIOSKeyboardLock } from '~hooks/useIOSKeyboardLock';
import { useSession } from '~hooks/useSession';
import { useRouter } from '~i18n/navigation';
import {
	dialogsApi,
	messagesAdapter,
	messagesSelector,
} from '~services/api/dialogs';
import { userApi } from '~services/api/user';
import { Loader } from '~shared/ui/atoms';

import { BotChatBackgroundImage } from '../BotChatBackgroundImage';
import { ChatImageGenerationLoader } from '../ChatImageGenerationLoader';
import { ConversationInput } from '../ConversationInput';
import { GiftFeed } from '../GiftFeed';
import {
	ConversationInner,
	ConversationLoader,
	ConversationOuter,
} from './styles';

const MemoisedMessagesToastContainer = memo(
	() => <ToastContainer hideProgressBar containerId="messages-toast" />,
	() => true
);
MemoisedMessagesToastContainer.displayName = 'MemoisedMessagesToastContainer';

export const Chat = ({ recipientId }: { recipientId: string }) => {
	const router = useRouter();
	const { userId, isSafeMode } = useSession();

	const { askForReview } = useChatReviewContext();

	const [omit, setOmit] = useState(0);
	const messagesSentCount = useRef(0);
	const participantRef = useRef<string>(recipientId);
	const pageRef = useIOSKeyboardLock();

	const { data: bots, isSuccess, isError } = useGetContacts();
	const [readMessage] = dialogsApi.useReadMessageMutation();

	const [sendWelcomeMessage] = userApi.useUserWelcomeMessageMutation();

	const skip =
		!bots?.contacts[recipientId] ||
		(participantRef.current !== recipientId && omit >= MESSAGES_COUNT);

	const {
		messages,
		isEnd,
		totalFetchedMessages,
		isLoading: isLoadingMessages,
		currentData: currentMessages,
		isFetching: isFetchingMessages,
		isError: isErrorMessages,
		isSuccess: isSuccessMessages,
	} = dialogsApi.useGetMessagesQuery(
		{
			recipientId,
			id: userId,
			omit,
		},
		{
			skip,
			refetchOnMountOrArgChange: 10 * 60 * 60,
			selectFromResult: ({ data, ...otherParams }) => {
				const messages = messagesSelector.selectAll(
					data?.messages ?? messagesAdapter.getInitialState()
				);
				const hasResponse = !!data?.receivedCount;
				const isEnd = hasResponse ? data.receivedCount < MESSAGES_COUNT : true;
				return {
					messages,
					totalFetchedMessages: data?.totalLength ?? 0,
					isEnd,
					...otherParams,
				};
			},
		}
	);

	const {
		botLastMessage,
		botTotalMessages,
		botUnreadMessages,
		bot,
		botLoading,
		botSuccess,
	} = useCurrentBot({
		recipientId,
	});

	const canAccessContent =
		bots?.contacts[recipientId] && (bot.visibility === 'sfw' || !isSafeMode);

	const hasMoreMessages =
		!!botTotalMessages && botTotalMessages > messages.length;

	const isChatLoading =
		isLoadingMessages ||
		botLoading ||
		// Fetching + no current data === loading of new cache entry => we're navigating to the conversation that haven't been loaded yet
		(isFetchingMessages && currentMessages === undefined);

	const setMessageOffset = useDebouncedCallback(
		(isTopReached: boolean) => {
			if (isFetchingMessages) {
				return;
			}
			if (!isEnd && isTopReached) {
				setOmit(totalFetchedMessages);
				return;
			}
		},
		[isEnd, totalFetchedMessages],
		200
	);

	annals.chat.useCopyPaste();

	useShowPaywall();

	useLayoutEffect(() => {
		setOmit(0);
		participantRef.current = recipientId; // TODO: Seems does not work, because new page reload every time
		messagesSentCount.current = 0;
	}, [recipientId]);

	useLayoutEffect(() => {
		if (
			((isSuccess || isError) && !bots?.contacts[recipientId]) ||
			isErrorMessages
		) {
			router.replace(routes.explore.models);
		}
	}, [isError, isSuccess, isErrorMessages, bots, router, recipientId]);

	useEffect(() => {
		if (
			botSuccess &&
			isSuccessMessages &&
			!isChatLoading &&
			messages.length === 0 &&
			!bot.preConditions
		) {
			void sendWelcomeMessage({
				userId,
				botId: recipientId,
			});
		}
	}, [
		bot.preConditions,
		botSuccess,
		isChatLoading,
		isSuccessMessages,
		messages.length,
		recipientId,
		sendWelcomeMessage,
		userId,
	]);

	useEffect(() => {
		if (!isChatLoading && botUnreadMessages > 0 && botLastMessage) {
			void readMessage({
				senderId: botLastMessage.senderId,
				recipientId: botLastMessage.recipientId,
				messageId: botLastMessage.id,
			});
		}
	}, [readMessage, isChatLoading, botUnreadMessages, botLastMessage]);

	useEffect(() => {
		return () => {
			const skip =
				messagesSentCount.current < NUMBER_OF_SENT_MESSAGES_FOR_REVIEW;
			askForReview({ skip, source: FeedbackTrigger.Implicit });
		};
	}, [recipientId, askForReview]);

	return (
		<>
			<BotPaywallContextProvider
				recipientId={recipientId}
				messagesLength={messages.length}
				checkoutReason={CheckoutReason.Chat}
			>
				<BotPaywallContextRecipient recipientId={recipientId} />
				<MediaContentContextProvider>
					<ConversationOuter className="chat-container" ref={pageRef}>
						<ConversationInner>
							<MessageListHeader recipientId={recipientId} />
							{isFetchingMessages && !isChatLoading && (
								<ConversationLoader>
									<Loader size={30} />
								</ConversationLoader>
							)}
							{isChatLoading || !canAccessContent ? (
								<Loader />
							) : (
								<>
									<MemoisedMessagesToastContainer />
									<MessageListV2
										key={`${recipientId}-${userId}-messages`}
										userId={userId}
										recipientId={recipientId}
										messages={messages}
										hasMoreMessages={hasMoreMessages}
										isLoading={isFetchingMessages}
										onScrollTop={setMessageOffset}
									/>
									<ChatImageGenerationLoader recipientId={recipientId} />
									<GiftFeed
										key={`${recipientId}-${userId}-gifts`}
										recipientId={recipientId}
										userId={userId}
									/>
									<ConversationInput
										recipientId={recipientId}
										userId={userId}
										onSend={() => {
											messagesSentCount.current += 1;
										}}
									/>
								</>
							)}
						</ConversationInner>
						<BotChatBackgroundImage />
					</ConversationOuter>
				</MediaContentContextProvider>
			</BotPaywallContextProvider>
		</>
	);
};
